import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Col,
  Container,
  Row,
  Card,
  CardBody,
  Input,
  Button,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
} from "reactstrap";

import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import BreadCrumb from "Components/Common/BreadCrumb";
import Loader from "Components/Common/Loader";
import { useFormik } from "formik";
import * as Yup from "yup";

import type { RootState } from "store";
import type { ThunkDispatch } from "redux-thunk";
import type { AnyAction } from "redux";
import UserMasterContainer from "./usermasterContainer";
import UserMasterForm from "./usermasterform";
import { handleValidDate, handleValidTime } from "helpers/common_helper";

import { toast } from "react-toastify";

import {
  listAdminUserThunk,
  addNewAdminUserThunk,
  fetchUserTypesThunk,
} from "slices/RBAC/thunk";
import { listAdminUser } from "services/rbacService";

import { Link, Navigate, useNavigate } from "react-router-dom";

const selectRBACState = (state: RootState) => (state as any).rbac;

const rbacSelector = createSelector(selectRBACState, (rbac) => ({
  adminUsers: rbac.adminUsers,
  adminUsersPagination: rbac.adminUsersPagination,
  userTypes: rbac.userTypes,
  loading: rbac.loading,
}));

const UserMasterPage: React.FC = () => {
  const dispatch: ThunkDispatch<RootState, any, AnyAction> = useDispatch();
  const { adminUsers, adminUsersPagination, userTypes, loading } = useSelector(rbacSelector);

  const adminUserList = adminUsers || [];

  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [usernameCheckTimeout, setUsernameCheckTimeout] = useState<NodeJS.Timeout | null>(null);
  const [phoneNoCheckTimeout, setPhoneNoCheckTimeout] = useState<NodeJS.Timeout | null>(null);
  const [emailIdCheckTimeout, setEmailIdCheckTimeout] = useState<NodeJS.Timeout | null>(null);

  const loadAdminUserList = useCallback(() => {
    return dispatch(
      listAdminUserThunk({
        page,
        per_page: limit,
        usermasterid: 0,
        usertypeid: 0,
        username: searchQuery.trim() || null,
        status: 0,
        email_id: null,
        phone_no: null,
      })
    );
  }, [dispatch, page, limit, searchQuery]);

  useEffect(() => {
    dispatch(fetchUserTypesThunk());
  }, [dispatch]);

  useEffect(() => {
    if (isModalOpen) return;
    const handler = setTimeout(() => {
      loadAdminUserList();
    }, 300);

    return () => clearTimeout(handler);
  }, [isModalOpen, loadAdminUserList]);

  useEffect(() => setPage(1), [searchQuery]);

  const navigate = useNavigate();

  const checkUsernameExists = useCallback(async (username: string): Promise<boolean> => {
    if (!username || username.trim().length < 2) {
      return false;
    }
    try {
      // Call service directly without updating Redux state
      const response: any = await listAdminUser({
        page: 1,
        per_page: 10,
        usermasterid: 0,
        usertypeid: 0,
        username: username.trim(),
        status: 0,
        email_id: null,
        phone_no: null,
      });
      if (response?.success && response?.data?.data?.users) {
        return response.data.data.users.length > 0;
      }
      return false;
    } catch (error) {
      console.error("Error checking username:", error);
      return false;
    }
  }, []);

  const checkPhoneNoExists = useCallback(async (phone_no: string): Promise<boolean> => {
    if (!phone_no || phone_no.trim().length < 6) {
      return false;
    }
    try {
      // Call service directly without updating Redux state
      const response: any = await listAdminUser({
        page: 1,
        per_page: 10,
        usermasterid: 0,
        usertypeid: 0,
        username: null,
        status: 0,
        email_id: null,
        phone_no: phone_no.trim(),
      });
      if (response?.success && response?.data?.data?.users) {
        return response.data.data.users.length > 0;
      }
      return false;
    } catch (error) {
      console.error("Error checking phone number:", error);
      return false;
    }
  }, []);

  const checkEmailIdExists = useCallback(async (email_id: string): Promise<boolean> => {
    if (!email_id || email_id.trim().length === 0) {
      return false;
    }
    try {
      // Call service directly without updating Redux state
      const response: any = await listAdminUser({
        page: 1,
        per_page: 10,
        usermasterid: 0,
        usertypeid: 0,
        username: null,
        status: 0,
        email_id: email_id.trim(),
        phone_no: null,
      });
      if (response?.success && response?.data?.data?.users) {
        return response.data.data.users.length > 0;
      }
      return false;
    } catch (error) {
      console.error("Error checking email ID:", error);
      return false;
    }
  }, []);

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        usertypeid: Yup.number().required("User type is required"),
        username: Yup.string()
          .required("Username is required")
          .matches(/^[a-zA-Z0-9/\s-]+$/, "Username can only contain alphanumeric characters, spaces, / and -")
          .test("username-exists", "Username already exists", async function (value) {
            if (!value || value.trim().length < 2) {
              return true;
            }
            const exists = await checkUsernameExists(value);
            return !exists;
          }),
        password: Yup.string()
          .required("Password is required")
          .min(8, "Password must be at least 8 characters")
          .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
          .matches(/[a-z]/, "Password must contain at least one lowercase letter")
          .matches(/[0-9]/, "Password must contain at least one number")
          .matches(/[^A-Za-z0-9]/, "Password must contain at least one special character"),
        phone_no: Yup.string()
          .required("Phone number is required")
          .matches(/^[0-9]{6,10}$/, "Phone number must be between 6 to 10 digits")
          .test("phone-exists", "Phone number already exists", async function (value) {
            if (!value || value.trim().length < 6) {
              return true;
            }
            const exists = await checkPhoneNoExists(value);
            return !exists;
          }),
        emailid: Yup.string()
          .required("Email ID is required")
          .email("Invalid email format")
          .max(70, "Email ID must not exceed 70 characters")
          .test("email-exists", "Email ID already exists", async function (value) {
            if (!value || value.trim().length === 0) {
              return true;
            }
            const exists = await checkEmailIdExists(value);
            return !exists;
          }),
      }),
    [checkUsernameExists, checkPhoneNoExists, checkEmailIdExists]
  );

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      usertypeid: "",
      username: "",
      password: "",
      phone_no: "",
      emailid: "",
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      // Close modal immediately
      setIsModalOpen(false);
      resetForm();

      try {
        const res = await dispatch(
          addNewAdminUserThunk({
            usertypeid: Number(values.usertypeid) || 0,
            username: values.username,
            password: values.password,
            phone_no: values.phone_no,
            emailid: values.emailid,
          })
        );
        if (!res?.success) {
          toast.error(res?.message || "Failed to add admin user");
          return;
        }
        toast.success("Admin user added successfully!");
        await loadAdminUserList();
        
        // Navigate to userrolemaster with the new usermasterid
        const usermasterid = (res as any)?.data || null;
        if (usermasterid) {
          navigate("/userrolemaster", { state: { usermasterid } });
        }
      } catch (err) {
        console.error("Error saving admin user:", err);
        toast.error("An error occurred. Please try again.");
      }
    },
  });

  const toggleModal = () => {
    if (!isModalOpen) {
      validation.resetForm();
      // Clear timeout when closing modal
      if (usernameCheckTimeout) {
        clearTimeout(usernameCheckTimeout);
        setUsernameCheckTimeout(null);
      }
    }
    setIsModalOpen(!isModalOpen);
  };

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (usernameCheckTimeout) {
        clearTimeout(usernameCheckTimeout);
      }
      if (phoneNoCheckTimeout) {
        clearTimeout(phoneNoCheckTimeout);
      }
      if (emailIdCheckTimeout) {
        clearTimeout(emailIdCheckTimeout);
      }
    };
  }, [usernameCheckTimeout, phoneNoCheckTimeout, emailIdCheckTimeout]);

  const handleRowClick = useCallback((row: any) => {}, []);

  const handleUsernameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const username = e.target.value;
    validation.handleChange(e);

    // Clear existing timeout
    if (usernameCheckTimeout) {
      clearTimeout(usernameCheckTimeout);
    }

    // Debounce the username check
    if (username && username.trim().length >= 2) {
      const timeout = setTimeout(() => {
        validation.setFieldTouched("username", true);
        validation.validateField("username");
      }, 500);
      setUsernameCheckTimeout(timeout);
    }
  }, [validation, usernameCheckTimeout]);

  const handlePhoneNoChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    let phone_no = e.target.value;
    // Only allow numbers
    phone_no = phone_no.replace(/[^0-9]/g, "");
    // Limit to 10 digits
    if (phone_no.length > 10) {
      phone_no = phone_no.substring(0, 10);
    }
    
    // Update the field value with filtered phone number
    validation.setFieldValue("phone_no", phone_no);

    // Clear existing timeout
    if (phoneNoCheckTimeout) {
      clearTimeout(phoneNoCheckTimeout);
    }

    // Debounce the phone number check
    if (phone_no && phone_no.trim().length >= 6) {
      const timeout = setTimeout(() => {
        validation.setFieldTouched("phone_no", true);
        validation.validateField("phone_no");
      }, 500);
      setPhoneNoCheckTimeout(timeout);
    }
  }, [validation, phoneNoCheckTimeout]);

  const handleEmailIdChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    let emailid = e.target.value;
    // Limit to 70 characters
    if (emailid.length > 70) {
      emailid = emailid.substring(0, 70);
    }
    validation.setFieldValue("emailid", emailid);

    // Clear existing timeout
    if (emailIdCheckTimeout) {
      clearTimeout(emailIdCheckTimeout);
    }

    // Debounce the email ID check
    if (emailid && emailid.trim().length > 0) {
      const timeout = setTimeout(() => {
        validation.setFieldTouched("emailid", true);
        validation.validateField("emailid");
      }, 500);
      setEmailIdCheckTimeout(timeout);
    }
  }, [validation, emailIdCheckTimeout]);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const columns = useMemo(
    () => [
      {
        header: "User ID",
        accessorKey: "usermasterid",
        cell: ({ row }: any) => {
          const id = row.original.usermasterid;
          return (
            <div className="text-center">
              {`#ID${String(id).padStart(3, "0")}`}
            </div>
          );
        },
      },
      {
        header: "Username",
        accessorKey: "username",
        cell: (cell: any) => {
          const value = cell.getValue();
          return <div className="text-center">{value || "-"}</div>;
        },
      },
      {
        header: "Phone Number",
        accessorKey: "phone_no",
        cell: (cell: any) => {
          const value = cell.getValue();
          return <div className="text-center">{value || "-"}</div>;
        },
      },
      {
        header: "Email ID",
        accessorKey: "email_id",
        cell: (cell: any) => {
          const value = cell.getValue();
          return <div className="text-center">{value || "-"}</div>;
        },
      },

      {
        header: "User Type",
        accessorKey: "user_type_name",
        cell: (cell: any) => {
          const value = cell.getValue();
          return <div className="text-center">{value || "-"}</div>;
        },
      },
      {
        header: "Created On",
        accessorKey: "createdon",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const value = cell.getValue();
          return (
            <div className="text-center">
              {value ? (
                <>
                  {handleValidDate(value)},{" "}
                  <small className="text-muted">{handleValidTime(value)}</small>
                </>
              ) : (
                <>-</>
              )}
            </div>
          );
        },
      },
      {
        header: "Created By",
        accessorKey: "createdby",
        cell: (cell: any) => {
          const value = cell.getValue();
          return (
            <div className="text-center">
              {value || "-"}
            </div>
          );
        },
      },
      {
        header: "Updated On",
        accessorKey: "updatedon",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const value = cell.getValue();
          return (
            <div className="text-center">
              {value ? (
                <>
                  {handleValidDate(value)},{" "}
                  <small className="text-muted">{handleValidTime(value)}</small>
                </>
              ) : (
                <>-</>
              )}
            </div>
          );
        },
      },
      {
        header: "Updated By",
        accessorKey: "updatedby",
        cell: (cell: any) => {
          const value = cell.getValue();
          return (
            <div className="text-center">
              {value || "-"}
            </div>
          );
        },
      },
      {
        header: "Status",
        accessorKey: "status",
        cell: (cell: any) => {
          const value = cell.getValue();
          const lowerVal = value?.toString().toLowerCase();
          const statusColor =
            lowerVal === "active" ? "#159E3A" : "#A65F00";

          return (
            <div className="text-center">
              <span
                style={{
                  display: "inline-block",
                  padding: "4px 12px",
                  borderRadius: "20px",
                  fontSize: "12px",
                  fontWeight: "500",
                  color: statusColor,
                  border: `1px solid ${statusColor}`,
                  textTransform: "capitalize",
                }}
              >
                {value}
              </span>
            </div>
          );
        },
      },
      {
        header: "Actions",
        accessorKey: "actions",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: any) => {
          const usermasterid = row.original.usermasterid;
          return (
            <div className="d-flex justify-content-center">
              <Button
                color="primary"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate("/userrolemaster", { 
                    state: { usermasterid } 
                  });
                }}
                className="btn-sm"
                title="AddRole/View"
              >
                AddRole/View
              </Button>
            </div>
          );
        },
      },
    ],
    [navigate]
  );

  return (
    <div className="page-content">
      <Container fluid className="border pt-4 rounded-4 bg-light mb-3 ms-3 pe-4">
        <BreadCrumb title="User Master" pageTitle={["RBAC", "User Master"]} />

        <Row className="align-items-center g-3 d-flex justify-content-between pb-4 pe-3">
          <Col md={3}>
            <div className="position-relative">
              <Input
                type="text"
                className="form-control border ps-5 ms-2 rounded-3"
                value={searchQuery}
                placeholder="Search User..."
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <i
                className="ri-search-line position-absolute top-50 start-0 translate-middle-y text-muted ms-3"
                style={{ fontSize: "1.1rem" }}
              />
            </div>
          </Col>
          <Col md={9} className="text-end">
            <Button color="primary" onClick={toggleModal}>
              + Add New
            </Button>
          </Col>
        </Row>

        <Row>
          <Col lg={12}>
            <Card className="border-0 shadow-none">
              <CardBody>
                {!loading ? (
                  adminUserList.length > 0 ? (
                    <UserMasterContainer
                      columns={columns}
                      data={adminUserList}
                      pagination={adminUsersPagination ? {
                        page: adminUsersPagination.page,
                        total_items: adminUsersPagination.total_items,
                        total_pages: adminUsersPagination.total_pages,
                        per_page: adminUsersPagination.per_page,
                      } : undefined}
                      limit={limit}
                      setLimit={setLimit}
                      setPage={setPage}
                      onPageChange={handlePageChange}
                      onRowClick={handleRowClick}
                      tableClass="align-middle border-bottom rounded-pill"
                      theadClass="bg-primary text-white"
                      divClass="table-responsive table-card"
                    />
                  ) : (
                    <div className="text-center p-4">
                      No records found.
                    </div>
                  )
                ) : (
                  <Loader />
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Add Modal */}
      <Modal isOpen={isModalOpen} toggle={toggleModal} centered size="lg">
        <ModalHeader toggle={toggleModal}>
          Add New User
        </ModalHeader>
        <form onSubmit={validation.handleSubmit}>
          <ModalBody>
            <UserMasterForm
              validation={validation}
              userTypes={userTypes}
              onUsernameChange={handleUsernameChange}
              onPhoneNoChange={handlePhoneNoChange}
              onEmailIdChange={handleEmailIdChange}
            />
          </ModalBody>
          <ModalFooter>
            <Button type="submit" color="primary">
              Add
            </Button>
          </ModalFooter>
        </form>
      </Modal>
    </div>
  );
};

export default UserMasterPage;
